import { LegalLayout, Clause } from "./LegalLayout";
import { RetentionAdminPanel } from "@/components/RetentionAdminPanel";
import { ShieldCheck } from "lucide-react";

const SCHEDULE = [
  ["Pay stubs and payroll runs", "7 years from pay date", "IRS §6001 · FLSA 29 CFR 516 · state wage rules"],
  ["Tax filings, W-2s and 1099s", "7 years from filing", "IRS §6501 assessment window plus margin"],
  ["Time entries and timesheets", "7 years from period end", "FLSA 3-year floor, extended to match payroll"],
  ["Funding events and payout ledger", "7 years from event", "Financial audit trail"],
  ["Audit log entries", "7 years from event", "Security and compliance forensics"],
  ["Employee identity records (SSN, address)", "7 years after separation", "Tax and wage-claim exposure"],
  ["Bank and card tokens", "Deleted on unlink or separation", "No reason to keep a payout token after the rail closes"],
  ["GPS clock-in coordinates", "24 months", "Geofence disputes only — not a long-term record"],
  ["Remote-tracking IP and geolocation", "12 months", "Shortest window that still supports a dispute"],
  ["Support and Buddy chat transcripts", "24 months", "Service quality"],
];

export default function LegalRetention() {
  return (
    <LegalLayout eyebrow="LEGAL · DATA RETENTION" title="Data Retention Policy" updated="2026-06-01" active="retention">
      <Clause n="1" heading="The seven-year rule">
        <p>
          Payroll records are evidence. The IRS expects employment tax records to be kept at least four years, the FLSA
          three, and several states demand more — so Checkmate standardizes on a single, defensible{" "}
          <b>seven-year retention period</b> measured from the pay date, period end, or event date of each record.
          Within that window, records are preserved and cannot be deleted by an ordinary user action.
        </p>
      </Clause>

      <Clause n="2" heading="Retention schedule">
        <div className="border-2 border-ink overflow-x-auto" data-testid="retention-schedule">
          <table className="w-full min-w-[560px] font-mono text-xs">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-3 py-2 uppercase tracking-widest">Record</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest">Retained</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest">Basis</th>
              </tr>
            </thead>
            <tbody>
              {SCHEDULE.map(([record, period, basis], i) => (
                <tr key={record} className={i % 2 ? "bg-[#F9F9F9]" : "bg-white"}>
                  <td className="px-3 py-2 border-t border-ink/20 font-semibold">{record}</td>
                  <td className="px-3 py-2 border-t border-ink/20">{period}</td>
                  <td className="px-3 py-2 border-t border-ink/20 text-muted-foreground">{basis}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Clause>

      <Clause n="3" heading="The Data Purge task">
        <p>
          A scheduled background task sweeps every tenant nightly and <b>flags</b> each record whose retention window has
          closed, stamping it with the date it became purge-eligible and the retention basis that expired. The sweep is
          idempotent and, by deliberate design, <b>non-destructive</b>: a machine never deletes payroll history.
        </p>
        <div className="border-2 border-ink p-4 flex gap-3 bg-secondary/40" data-testid="retention-purge-flow">
          <ShieldCheck className="w-6 h-6 shrink-0" />
          <div className="text-xs leading-relaxed">
            <div className="font-black text-sm mb-1">Flag → review → deliberate purge</div>
            <ol className="list-decimal pl-4 space-y-1">
              <li>Nightly sweep flags expired records as purge-eligible.</li>
              <li>Administrators see the flagged inventory per record type, plus what is within six months of expiring.</li>
              <li>Deletion happens only when a human reviews and confirms it — and is itself written to the audit log.</li>
              <li>Anything under litigation hold, an open wage claim, or an active tax examination is excluded from purge.</li>
            </ol>
          </div>
        </div>
      </Clause>

      <Clause n="4" heading="Early deletion and the right to be forgotten">
        <p>
          A deletion request under GDPR Art. 17 or CCPA §1798.105 is honoured immediately for everything not subject to a
          statutory hold: your login, profile, contact details, uploaded documents, GPS history and payment tokens.
          Payroll history your employer is legally required to keep is <b>anonymized rather than destroyed</b> — the
          financial record survives for the tax authority, stripped of the identifiers that point to you.
        </p>
      </Clause>

      <Clause n="5" heading="Backups and processors">
        <p>
          Encrypted backups follow a rolling 35-day window; a record deleted from the live database ages out of backups
          within that period. Processors keep their own records under their own schedules: Stripe and Plaid retain
          transaction data for the period their financial regulators require, independent of this policy.
        </p>
      </Clause>

      <Clause n="6" heading="Questions">
        <p>Retention, litigation-hold and purge requests: <b>privacy@checkmatepayrolls.com</b>.</p>
      </Clause>

      <RetentionAdminPanel />
    </LegalLayout>
  );
}
